import { Pipe, PipeTransform } from '@angular/core';

interface Division {
  readonly amount: number;
  readonly unit: Intl.RelativeTimeFormatUnit;
}

// Aufsteigende Einheiten: Sekunden → Jahre. `amount` ist jeweils der Umrechnungs-
// faktor zur nächstgrößeren Einheit.
const DIVISIONS: readonly Division[] = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

/**
 * Wandelt einen ISO-Zeitstempel in eine locale-bewusste relative Angabe
 * („vor 2 Stunden", „gestern", „2 hours ago"). Nutzt das eingebaute
 * `Intl.RelativeTimeFormat`, daher ohne zusätzliche Dependency. Die Locale
 * wird als Argument übergeben, damit die Pipe rein bleibt und bei
 * Sprachwechsel neu ausgewertet wird.
 *
 * Bewusst `pure` (Default): die Liste tickt nicht in Echtzeit, daher wird die
 * relative Angabe erst bei der nächsten Change-Detection (z. B. Reload, Filter,
 * Sprachwechsel) aktualisiert. `pure: false` würde pro CD-Zyklus neu berechnen
 * und ist den Live-Refresh hier nicht wert.
 */
@Pipe({ name: 'relativeTime', standalone: true })
export class RelativeTimePipe implements PipeTransform {
  transform(value: string | Date | null | undefined, locale?: string): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const date = value instanceof Date ? value : new Date(value);
    const ms = date.getTime();
    if (Number.isNaN(ms)) {
      return '';
    }
    const formatter = new Intl.RelativeTimeFormat(locale || 'de', { numeric: 'auto' });
    let duration = (ms - Date.now()) / 1000;
    for (const division of DIVISIONS) {
      if (Math.abs(duration) < division.amount) {
        return formatter.format(Math.round(duration), division.unit);
      }
      duration /= division.amount;
    }
    return formatter.format(Math.round(duration), 'year');
  }
}
